/**
 * ContactFormBlock — bloc CMS de formulaire de contact.
 * Reproduit le comportement de l'ancienne ContactPage (Turnstile captcha,
 * validations, POST /api/public/contact).
 * Options (d):
 *   - title, subtitle (optionnel)
 *   - show_details (bool, default true) — affiche adresse/email/téléphone en colonne latérale
 *   - address, email, phone (optionnel, sinon lus depuis les variables contact.*)
 *   - interests: [{value,label}, ...] (optionnel — sinon liste par défaut)
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { getPartnerRef, trackGenerateLead } from "../../lib/events";
import { useSiteSettings } from "../../lib/siteSettings";

const DEFAULT_INTERESTS = [
  { value: "hebergement", label: "Séjour / hébergement" },
  { value: "mariage", label: "Mariage" },
  { value: "seminaire", label: "Séminaire / réunion" },
  { value: "haras", label: "Haras / équitation" },
  { value: "autre", label: "Autre" },
];

function TurnstileWidget({ siteKey, onToken }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!siteKey) return;
    const scriptId = "cf-turnstile-js";
    const init = () => {
      if (window.turnstile && ref.current) {
        ref.current.replaceChildren();
        window.turnstile.render(ref.current, { sitekey: siteKey, callback: onToken, theme: "light" });
      }
    };
    if (document.getElementById(scriptId)) { init(); return; }
    const s = document.createElement("script");
    s.id = scriptId;
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true; s.defer = true;
    s.onload = init;
    document.body.appendChild(s);
  }, [siteKey, onToken]);
  if (!siteKey) return null;
  return <div ref={ref} data-testid="turnstile-widget" className="my-3" />;
}

export default function ContactFormBlock({ d = {}, vars = {} }) {
  const [cfg, setCfg] = useState(null);
  const _qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "",
    subject: _qs.get("sujet") || "", message: _qs.get("message") || "" });
  const [captchaToken, setCaptchaToken] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/public/contact/config")
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ captcha_enabled: false }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    console.log("[ContactForm] submit start", { hasCaptcha: !!captchaToken, cfg });
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 10) {
      toast.error("Nom, email et message (10+ caractères) requis");
      return;
    }
    if (cfg?.captcha_enabled && !captchaToken) {
      toast.error("Merci de valider le captcha ci-dessous avant d'envoyer");
      console.warn("[ContactForm] blocked: captcha token missing");
      return;
    }
    setSending(true);
    // Timeout de sécurité : si le POST reste bloqué > 15s, on relâche le spinner
    const safetyTimeout = setTimeout(() => {
      setSending(false);
      toast.error("Envoi trop long — vérifiez votre connexion et réessayez");
      console.error("[ContactForm] timeout after 15s");
    }, 15000);
    try {
      const res = await api.post("/public/contact", { ...form, captcha_token: captchaToken, partner_ref: getPartnerRef() });
      clearTimeout(safetyTimeout);
      console.log("[ContactForm] success", res?.data);
      trackGenerateLead({ lead_type: form.interest, form_id: "contact_general" });
      setSent(true);
      toast.success("Message envoyé");
    } catch (err) {
      clearTimeout(safetyTimeout);
      const detail = err?.response?.data?.detail || err?.message || "Erreur d'envoi";
      console.error("[ContactForm] error", { status: err?.response?.status, detail, err });
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const interests = d.interests || DEFAULT_INTERESTS;
  const showDetails = d.show_details !== false;
  const hideEmail = d.hide_email !== false; // par défaut on masque l'email pour éviter le spam & centraliser sur formulaire
  const settings = useSiteSettings();
  const address = d.address || settings["contact.address_fr"] || "Domaine de la Briouse — Sanxay, 86600 Vienne, France";
  const email = d.email || settings["contact.email"] || "";
  const phone = d.phone || settings["contact.phone"] || "";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="cms-contact-form">
      {showDetails && (
        <div className="lg:col-span-1 space-y-6" data-testid="contact-details">
          <div>
            <p className="eyebrow mb-3">Adresse</p>
            <div className="flex gap-3">
              <MapPin size={18} className="text-domaine flex-shrink-0 mt-1" />
              <p className="text-ardoise/80 text-sm whitespace-pre-line">{address}</p>
            </div>
          </div>
          {!hideEmail && email && (
            <div>
              <p className="eyebrow mb-3">Email</p>
              <div className="flex gap-3">
                <Mail size={18} className="text-domaine flex-shrink-0 mt-1" />
                <a href={`mailto:${email}`} className="text-ardoise/80 hover:text-domaine text-sm">{email}</a>
              </div>
            </div>
          )}
          {phone && (
            <div>
              <p className="eyebrow mb-3">Téléphone</p>
              <div className="flex gap-3">
                <Phone size={18} className="text-domaine flex-shrink-0 mt-1" />
                <a href={`tel:${phone.replace(/[\s.]/g, "")}`} className="text-ardoise/80 hover:text-domaine text-sm">{phone}</a>
              </div>
            </div>
          )}
        </div>
      )}

      <div className={showDetails ? "lg:col-span-2 border border-pierre bg-white p-6 md:p-8" : "lg:col-span-3 border border-pierre bg-white p-6 md:p-8"} data-testid="contact-form-block">
        {sent ? (
          <div className="text-center py-12" data-testid="contact-form-sent">
            <p className="font-serif text-2xl text-domaine mb-3">Merci !</p>
            <p className="text-ardoise/70">Votre message a bien été envoyé. Nous vous répondrons rapidement à <strong>{form.email}</strong>.</p>
            <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", interest: "", subject: "", message: "" }); }}
                    className="btn-ghost-domaine mt-6">
              Envoyer un autre message
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-ardoise/70 mb-1">Nom complet *</label>
                <input required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="w-full border border-pierre px-3 py-2 focus:outline-none focus:border-domaine" data-testid="contact-name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-ardoise/70 mb-1">Email *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="w-full border border-pierre px-3 py-2 focus:outline-none focus:border-domaine" data-testid="contact-email" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-ardoise/70 mb-1">Téléphone</label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       className="w-full border border-pierre px-3 py-2 focus:outline-none focus:border-domaine" data-testid="contact-phone" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-ardoise/70 mb-1">Motif</label>
                <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}
                        className="w-full border border-pierre px-3 py-2 focus:outline-none focus:border-domaine" data-testid="contact-interest">
                  <option value="">— Choisir —</option>
                  {interests.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-ardoise/70 mb-1">Sujet</label>
              <input type="text" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                     className="w-full border border-pierre px-3 py-2 focus:outline-none focus:border-domaine" data-testid="contact-subject" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-ardoise/70 mb-1">Message *</label>
              <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full border border-pierre px-3 py-2 focus:outline-none focus:border-domaine resize-none" data-testid="contact-message" />
              <p className="text-xs text-ardoise/50 mt-1">{form.message.length}/5000</p>
            </div>

            {cfg?.captcha_enabled && cfg?.captcha_site_key && (
              <TurnstileWidget siteKey={cfg.captcha_site_key} onToken={setCaptchaToken} />
            )}

            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-ardoise/50">Vos données sont utilisées uniquement pour vous répondre.</p>
              <button type="submit" disabled={sending || !cfg}
                      className="btn-domaine inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit"
                      title={!cfg ? "Chargement de la configuration…" : (sending ? "Envoi en cours…" : "Envoyer")}>
                {sending ? <><Loader2 size={14} className="animate-spin" /> Envoi…</> : <><Send size={14} /> Envoyer</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
